import { DateInput, DateSegment, TimeField as RACTimeField } from "react-aria-components";
import { type CSSProperties, useId } from "react";
import { parseTime, Time } from "@internationalized/date";

import "./datetime.css";

interface TimeFieldProps {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  ariaLabel?: string;
  required?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  style?: CSSProperties;
}

function safeParse(value: string): Time | null {
  if (!value) return null;
  try {
    return parseTime(value.length === 5 ? `${value}:00` : value);
  } catch {
    return null;
  }
}

function formatHHMM(time: Time | null): string {
  if (!time) return "";
  const hh = String(time.hour).padStart(2, "0");
  const mm = String(time.minute).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function TimeField({
  value,
  onChange,
  id,
  ariaLabel,
  required,
  disabled,
  invalid = false,
  className,
  style,
}: TimeFieldProps) {
  const timeValue = safeParse(value);
  const classes = ["datefield", invalid ? "datefield--invalid" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <RACTimeField
      id={fieldId}
      value={timeValue}
      onChange={(v) => onChange(v instanceof Time ? formatHHMM(v) : "")}
      aria-label={ariaLabel}
      isRequired={required}
      isDisabled={disabled}
      hourCycle={24}
      granularity="minute"
    >
      <DateInput className={`${classes} datefield--time`} style={style}>
        {(segment) => <DateSegment segment={segment} className="datefield__seg" />}
      </DateInput>
    </RACTimeField>
  );
}
